import { Assert } from "../../Assert";
import { DataSourcePropertyValueChange } from "../../Data/DataSourcePropertyValueChange";
import { Nullable, PartialNullable } from "../../Extensions";
import { PathHelper } from "../../Helpers/PathHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { DetailsPage } from "../Pages/DetailsPage";
import { ComboBoxApplicationEntity } from "./ComboBoxApplicationEntity";
import { IPropertyEditorComboBox, PropertyEditorComboBox, PropertyEditorComboBoxPropertyValues } from "./PropertyEditorComboBox";
import { NAMESPACE } from "./_Namespace";

export type PropertyEditorComboBoxApplicationEntityPropertyValues =
    PropertyEditorComboBoxPropertyValues
    & {
        extraProperties: string[];
        foreignEntityAlias: string;
        primaryEntity: string;
        primaryEntityDescriptionProperty: string;
        primaryEntityFilter: string;
        primaryEntityOrderProperty: string;
    };

export type PropertyEditorComboBoxApplicationEntityProperties = {
    [name in keyof PropertyEditorComboBoxApplicationEntityPropertyValues]: Property<PropertyEditorComboBoxApplicationEntityPropertyValues[name]>
};

export interface IPropertyEditorComboBoxApplicationEntity extends IPropertyEditorComboBox, PropertyEditorComboBoxApplicationEntityProperties
{
}

export class PropertyEditorComboBoxApplicationEntity extends PropertyEditorComboBox implements IPropertyEditorComboBoxApplicationEntity
{
    // #region Static

    public static override classNamespace = NAMESPACE + "PropertyEditorComboBoxApplicationEntity";
    public static override className = "PropertyEditorComboBoxApplicationEntity";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<PropertyEditorComboBoxApplicationEntityPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                createEditorControl: () => new ComboBoxApplicationEntity(),
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public extraProperties!: Property<string[]>;
    public foreignEntityAlias!: PropertyString;
    public primaryEntity!: PropertyString;
    public primaryEntityDescriptionProperty!: PropertyString;
    public primaryEntityFilter!: PropertyString;
    public primaryEntityOrderProperty!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<PropertyEditorComboBoxApplicationEntityPropertyValues>)
    {
        super(propertyValues);

        // Trigger Property Changes
        this.extraProperties.triggerChange();
        this.primaryEntity.triggerChange();
        this.primaryEntityDescriptionProperty.triggerChange();
        this.primaryEntityFilter.triggerChange();
        this.primaryEntityOrderProperty.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _extraPropertiesChanged(change: PropertyChange<string[]>)
    {
        (this.editorControl.get() as Nullable<ComboBoxApplicationEntity>)?.extraProperties.set(change.currentValue);
    }
    protected _foreignEntityAliasChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _hideIfPathControl()
    {
        const
            page = this.page.get();

        if (!(page instanceof DetailsPage))
            return;

        const
            pageDataSource = page.dataSource.get(),
            dataSourceProperty = this.dataSourceProperty.get();

        if (!pageDataSource)
        {
            this.visible.set(true);
            return;
        }

        const
            url = page.url.get(),
            location = url ? PathHelper.getLocation(url) : null,
            pathParts = location?.applicationRelativeUrl ? StringHelper.trimEnd(location.applicationRelativeUrl, "/").split("/") : [],
            primaryEntity = this.primaryEntity.get(),
            foreignEntityAlias = this.foreignEntityAlias.get();

        if (primaryEntity && foreignEntityAlias)
        {
            if
                (
                pathParts.length === 4
                && StringHelper.equals(pathParts[0], primaryEntity, { ignoreCase: true })
                && StringHelper.equals(pathParts[2], foreignEntityAlias, { ignoreCase: true })
            )
            {
                if (dataSourceProperty)
                {
                    page.dataSourcePropertyFromUrl = dataSourceProperty;
                    pageDataSource.propertyValueByPathSet(dataSourceProperty, pathParts[1]);
                }

                page._loadApplicationEntityPropertiesAsync(pageDataSource);

                this.visible.set(false);
            }
            else
                this.visible.set(true);
        }
    }
    protected _primaryEntityChanged(change: PropertyChange<string>)
    {
        const
            editorControl = this.editorControl.get();

        Assert.isInstanceOf(ComboBoxApplicationEntity, editorControl, "editorControl");

        editorControl.primaryEntity.set(change.currentValue);
    }
    protected _primaryEntityDescriptionPropertyChanged(change: PropertyChange<string>)
    {
        const
            editorControl = this.editorControl.get();

        Assert.isInstanceOf(ComboBoxApplicationEntity, editorControl, "editorControl");

        editorControl.primaryEntityDescriptionProperty.set(change.currentValue);
    }
    protected _primaryEntityFilterChanged(change: PropertyChange<string>)
    {
        const
            editorControl = this.editorControl.get();

        Assert.isInstanceOf(ComboBoxApplicationEntity, editorControl, "editorControl");

        editorControl.primaryEntityFilter.set(change.currentValue);
    }
    protected _primaryEntityOrderPropertyChanged(change: PropertyChange<string>)
    {
        const
            editorControl = this.editorControl.get();

        Assert.isInstanceOf(ComboBoxApplicationEntity, editorControl, "editorControl");

        editorControl.primaryEntityOrderProperty.set(change.currentValue);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: PropertyEditorComboBoxApplicationEntityPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.extraProperties = new Property<string[]>(this, "System.String[], System.Private.CoreLib", propertyValues.extraProperties, this._extraPropertiesChanged);
        this.foreignEntityAlias = new PropertyString({
            context: this,
            initialValue: propertyValues.foreignEntityAlias,
            changeHandler: this._foreignEntityAliasChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Foreign Entity Alias" }),
            ],
        });
        this.primaryEntity = new PropertyString({
            context: this,
            initialValue: propertyValues.primaryEntity,
            changeHandler: this._primaryEntityChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Primary Entity" }),
            ],
        });
        this.primaryEntityDescriptionProperty = new PropertyString({
            context: this,
            initialValue: propertyValues.primaryEntityDescriptionProperty,
            changeHandler: this._primaryEntityDescriptionPropertyChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Primary Entity Description Property" }),
            ],
        });
        this.primaryEntityFilter = new PropertyString({
            context: this,
            initialValue: propertyValues.primaryEntityFilter,
            changeHandler: this._primaryEntityFilterChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Primary Entity Filter" }),
            ],
        });
        this.primaryEntityOrderProperty = new PropertyString({
            context: this,
            initialValue: propertyValues.primaryEntityOrderProperty,
            changeHandler: this._primaryEntityOrderPropertyChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Primary Entity Order Property" }),
            ],
        });
    }

    public override loadDataAsync()
    {
        this._hideIfPathControl();

        const
            editorControl = this.editorControl.get();

        Assert.isInstanceOf(ComboBoxApplicationEntity, editorControl, "editorControl");

        return editorControl.loadDataAsync();
    }
    protected override onDataSourcePropertyValueChanged(evt: IJNativeEvent, change: DataSourcePropertyValueChange<any>)
    {
        const
            self = evt.data.self as this,
            primaryEntityFilter = self.primaryEntityFilter.get();

        if (primaryEntityFilter && primaryEntityFilter.indexOf(StringHelper.format("[{0}]", change.path)) >= 0)
        {
            self.value.set(null);
            setTimeout(() => { self.loadDataAsync(); });
        }
    }

    // #endregion

    // #endregion
}
